<script setup lang="ts">
import MissionReward from '../items/MissionReward.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  mission: TMissionOrBadge;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isRewardModalVisible,
  rewardData,
  buttonState,
  handleButtonClick,
  handleMissionClick,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));
</script>

<template>
  <div class="mission-card">
    <!-- Header: Imagem + Info -->
    <div class="mission-card__header">
      <SmarticoApostatudoSmarticoImage
        :alt="mission.name"
        class="mission-card__image"
        :src="mission.image"
      />
      <div class="mission-card__info">
        <div
          class="mission-card__title"
          @click="handleMissionClick('reward')"
        >
          {{ mission.name }}
        </div>
        <div
          class="mission-card__description"
          @click="handleMissionClick('reward')"
        >
          {{ mission.description }}
        </div>
      </div>
    </div>

    <!-- Prêmio -->
    <div
      v-if="rewardData.reward"
      class="mission-card__reward"
      @click="handleMissionClick('reward')"
    >
      <div class="flex gap-2 items-center justify-center">
        <NuxtIcon
          class="mission-card__reward-icon"
          :filled="true"
          name="mission/gift"
        />
        <span class="mission-card__reward-label">PRÊMIO</span>
      </div>
      <span class="mission-card__reward-value">{{ rewardData.reward }}</span>
    </div>

    <!-- Botão -->
    <button
      v-if="buttonState"
      class="mission-card__button"
      :class="{
        'mission-card__button--participating':
          buttonState.text === 'Participando',
      }"
      :disabled="buttonState.disabled"
      @click.stop="handleButtonClick"
    >
      <span>{{ buttonState.text }}</span>
      <NuxtIcon
        v-if="buttonState.text !== 'Participando'"
        name="mission/button/participar"
      />
    </button>

    <!-- Modal de Reward -->
    <MissionReward
      v-model:modal-visible="isRewardModalVisible"
      :mission="mission"
    />
  </div>
</template>

<style scoped>
.mission-card {
  width: 212px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
}

/* Header: Imagem + Info */
.mission-card__header {
  display: flex;
  align-items: normal;
  gap: 8px;
  flex-shrink: 0;
}

.mission-card__image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  object-fit: cover;
}

.mission-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mission-card__title {
  font-size: 12px;
  font-weight: 700;
  color: white;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  cursor: pointer;
  line-height: 1.25;
}

.mission-card__title:hover {
  opacity: 0.8;
}

.mission-card__description {
  font-size: 10px;
  font-weight: 400;
  color: #a1a1aa;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  cursor: pointer;
  line-height: 1.3;
}

.mission-card__description:hover {
  opacity: 0.8;
}

/* Prêmio */
.mission-card__reward {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: rgba(0, 201, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}

.mission-card__reward:hover {
  background: rgba(0, 201, 255, 0.15);
}

.mission-card__reward-icon {
  font-size: 16px;
  color: #00c9ff;
  flex-shrink: 0;
}

.mission-card__reward-label {
  font-size: 8px;
  font-weight: 600;
  color: rgba(255, 255, 255, 1);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.mission-card__reward-value {
  font-size: 11px;
  font-weight: 700;
  color: #ff9500;
  white-space: nowrap;
  overflow: hidden;
  max-width: 180px;
  text-overflow: ellipsis;
}

/* Botão */
.mission-card__button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  padding: 8px;
  background: var(--accent-color, #1414dd);
  border: none;
  border-radius: 4.5px;
  color: white;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s;
  margin-top: auto;
  flex-shrink: 0;
}

.mission-card__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mission-card__button:hover:not(:disabled) {
  filter: brightness(1.1);
}

.mission-card__button--participating {
  background: rgba(23, 201, 100, 0.1);
  border: 1px solid rgba(23, 201, 100, 0.5);
  color: #17c964;
}
</style>
